import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import type { DpEmployeeDetail } from "../api/employeesApi";
import { getStoresPaginatedApi, StorePublic } from "../../store/storeApi";
import {
  DpSchedulePreference,
  DpShiftPreference,
  bulkAssignStoreApi,
  unassignStoreApi,
  getSchedulePreferenceApi,
  saveSchedulePreferenceApi,
  SHIFT_PREFERENCE_LABELS,
  WEEKDAY_LABELS,
} from "../api/scheduleApi";

type Props = {
  token: string;
  employee: DpEmployeeDetail;
  /** Não usado desde que a loja virou vínculo atual (troca na própria aba). */
  onOpenContract?: () => void;
};

/**
 * Aba "Escala" da ficha: a loja em que o colaborador está lotado e as
 * preferências que a escala mensal respeita (turno e dia fixo de folga).
 */
export default function EmployeeScheduleTab({ token, employee }: Props) {
  const [preference, setPreference] = useState<DpSchedulePreference | null>(null);
  const [form, setForm] = useState({
    shift: "ROTATIVO" as DpShiftPreference,
    fixedDayOff: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [stores, setStores] = useState<StorePublic[]>([]);

  useEffect(() => {
    getStoresPaginatedApi(token, { all: true })
      .then((res) => setStores(res.items ?? []))
      .catch(() => setStores([]));
  }, [token]);

  /** Troca a loja na hora (sem data): lota na escolhida ou retira da atual. */
  const changeStore = async (storeId: string) => {
    if (!preference) return;
    setSaving(true);
    try {
      if (storeId) await bulkAssignStoreApi(token, { storeId, employeeIds: [employee.id] });
      else if (preference.store) await unassignStoreApi(token, { storeId: preference.store.id, employeeIds: [employee.id] });
      toast.success(storeId ? "Loja alterada." : "Colaborador retirado da loja.");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao trocar a loja.");
    } finally {
      setSaving(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const current = await getSchedulePreferenceApi(token, employee.id);
      setPreference(current);
      setForm({
        shift: current.shift,
        fixedDayOff: current.fixedDayOff === null ? "" : String(current.fixedDayOff),
        notes: current.notes ?? "",
      });
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar a escala do colaborador.");
    }
  }, [token, employee.id]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveSchedulePreferenceApi(token, employee.id, {
        shift: form.shift,
        fixedDayOff: form.fixedDayOff === "" ? null : Number(form.fixedDayOff),
        notes: form.notes,
      });
      toast.success("Escala do colaborador salva.");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar a escala do colaborador.");
    } finally {
      setSaving(false);
    }
  };

  if (!preference) return <div className="dpEmpty">Carregando...</div>;

  const terminated = employee.status === "DESLIGADO";

  return (
    <section className="dpSection">
      <h2 className="dpSection__title">Escala</h2>

      <div className="dpFormGrid" style={{ marginBottom: 16 }}>
        <div className="dpFormGroup">
          <label htmlFor="scheduleStore">Loja</label>
          {preference.hasActiveContract ? (
            <select
              id="scheduleStore"
              className="dpSelect"
              value={preference.store?.id ?? ""}
              onChange={(e) => changeStore(e.target.value)}
              disabled={saving || terminated}
            >
              <option value="">Sem loja</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nomeFantasia}
                </option>
              ))}
            </select>
          ) : (
            <div className="dpDefinition__value">Sem contrato ativo</div>
          )}
          <span className="dpFormHint">Vale na hora: o colaborador entra (ou sai) da escala da loja na próxima sugestão.</span>
        </div>
      </div>

      {preference.hasActiveContract && !preference.store ? (
        <p className="dpNotice dpNotice--warning">Sem loja, o colaborador não entra na escala de nenhuma loja.</p>
      ) : null}

      <form onSubmit={save}>
        <div className="dpFormGrid">
          <div className="dpFormGroup">
            <label htmlFor="scheduleShift">Turno</label>
            <select
              id="scheduleShift"
              className="dpSelect"
              value={form.shift}
              onChange={(e) => setForm({ ...form, shift: e.target.value as DpShiftPreference })}
              disabled={saving || terminated}
            >
              {(Object.keys(SHIFT_PREFERENCE_LABELS) as DpShiftPreference[]).map((shift) => (
                <option key={shift} value={shift}>
                  {SHIFT_PREFERENCE_LABELS[shift]}
                </option>
              ))}
            </select>
          </div>
          <div className="dpFormGroup">
            <label htmlFor="scheduleDayOff">Dia fixo de folga</label>
            <select
              id="scheduleDayOff"
              className="dpSelect"
              value={form.fixedDayOff}
              onChange={(e) => setForm({ ...form, fixedDayOff: e.target.value })}
              disabled={saving || terminated}
            >
              <option value="">Sem dia fixo (o sistema sugere)</option>
              {WEEKDAY_LABELS.map((label, index) => {
                const blocked = preference.noDayOffWeekdays.includes(index);
                return (
                  <option key={label} value={index} disabled={blocked}>
                    {label}
                    {blocked ? " (a loja não dá folga)" : ""}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="dpFormGroup" style={{ gridColumn: "1 / -1" }}>
            <label htmlFor="scheduleNotes">Observação</label>
            <textarea
              id="scheduleNotes"
              className="dpTextarea"
              maxLength={500}
              rows={2}
              placeholder="Ex.: estuda à noite; só manhã até dezembro."
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              disabled={saving || terminated}
            />
          </div>
        </div>
        <p className="dpFormHint">
          {preference.isDefault
            ? "Ainda sem preferência salva: vale rotativo, sem dia fixo de folga."
            : `Última alteração por ${preference.updatedBy?.nomeCompleto ?? "—"}.`}
        </p>
        {!terminated ? (
          <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
            Salvar
          </button>
        ) : null}
      </form>
    </section>
  );
}
